'use client';
import {
  IconFileDescription,
  IconMapPin,
  IconUserShield,
} from '@tabler/icons-react';
import {
  FetchClaimDetailResponseBody,
  FetchInsurerClaimDetailResponseBody,
  SearchEntityRequestQuery,
  SearchEntityResponseBody,
  UpdateClaimIntakeRequestBody,
  UpdateClaimRequestBody,
} from 'bff';
import { format } from 'date-fns';
import { cn } from 'ui';
import {
  currencyFormat,
  getCountryLabel,
  getStateLabel,
  toTitleCase,
} from 'utils';
import {
  ClaimUpdateContext,
  ClaimUpdateProvider,
} from '../contexts/ClaimUpdateContext';
import { EditClaimButton } from './EditClaimButton';
import { ClaimSummary, SinisterLocationMap } from 'shared-components';
import { ClaimIntakeCard, ClaimIntakeFetchers } from './ClaimIntakeCard';
import {
  ClaimSummaryCardKey,
  DEFAULT_COUNTRY_CODE,
  countryAndStates,
  getClaimPresentationProfile,
} from 'piramid-constants';

type Document = FetchClaimDetailResponseBody['documents'][number];

export const ClaimDetailCards = ({
  data,
  onOpenDocument,
  withEdit = false,
  onSave,
  onInvalidate,
  fetchers,
  searchEntity,
  onSaveIntake,
  intakeFetchers,
}: {
  data: FetchInsurerClaimDetailResponseBody;
  onOpenDocument?: (document: Document) => void;
  withEdit?: boolean;
  onSave?: (data: UpdateClaimRequestBody) => Promise<any>;
  onInvalidate?: () => void;
  fetchers?: ClaimUpdateContext['fetchers'];
  searchEntity?: (
    query: SearchEntityRequestQuery,
  ) => Promise<SearchEntityResponseBody>;
  // Intake card: read-only unless both a save handler and pickers arrive.
  onSaveIntake?: (body: UpdateClaimIntakeRequestBody) => Promise<unknown>;
  intakeFetchers?: ClaimIntakeFetchers;
}) => {
  const presentationProfile = getClaimPresentationProfile(
    data.entrypoint_backend,
  );
  const showCard = (key: ClaimSummaryCardKey) =>
    presentationProfile.summaryCards.includes(key);

  const [intakeSubmission] = data.intake_submissions ?? [];

  const desiredSupportLocationState = countryAndStates
    .find((c) => c.code === DEFAULT_COUNTRY_CODE)
    ?.states.find((s) => s.code === data.desired_support_location_state);

  // The sinister's location can only be edited while the pre-claim has NOT yet
  // been created in the provider (Rivadavia).
  const canEditOccurrenceLocation = !data.external_claim_request_id;

  return (
    <ClaimUpdateProvider
      enabled={withEdit}
      data={data}
      onInvalidate={onInvalidate || (() => {})}
      onSave={onSave || (() => Promise.resolve())}
      fetchers={fetchers}
    >
      <ClaimSummary.CardList>
        {showCard('victim') && (
          <ClaimSummary.CardGroup className='col-span-1'>
            <ClaimSummary.Header>
              <div className='flex items-center gap-2'>
                <ClaimSummary.CardGroupLabel>
                  <ClaimSummary.Icon>
                    <IconUserShield />
                  </ClaimSummary.Icon>
                  Damnificado
                </ClaimSummary.CardGroupLabel>

                <ClaimSummary.VictimMinorBadge />
              </div>

              {withEdit && <EditClaimButton data={data} segment='victim' />}
            </ClaimSummary.Header>
            <ClaimSummary.CardGroupSeparator />
            <ClaimSummary.CardGroupFields className={cn('grid grid-cols-1')}>
              <ClaimSummary.Victim segment='victim' data={data.victim!} />
              <ClaimSummary.RecoveryDamagedPerson damages={data.damages!} />
            </ClaimSummary.CardGroupFields>
          </ClaimSummary.CardGroup>
        )}

        {showCard('claimant') && (data.claimants || []).length > 0 && (
          <ClaimSummary.Claimants
            claimants={data.claimants!}
            ownerEmail={data.owner?.email}
            ownershipTransfers={data.ownership_transfers}
          />
        )}

        {showCard('damages') && (
          <ClaimSummary.Damages
            damages={data.damages!}
            searchEntity={searchEntity}
            insurerName={data.entrypoint?.name}
          />
        )}

        {showCard('occurrence') && (
          <ClaimSummary.CardGroup>
            <ClaimSummary.Header>
              <ClaimSummary.CardGroupLabel>
                <ClaimSummary.Icon>
                  <IconFileDescription />
                </ClaimSummary.Icon>
                Datos del siniestro
              </ClaimSummary.CardGroupLabel>
              {withEdit && <EditClaimButton data={data} segment='occurrence' />}
            </ClaimSummary.Header>

            <ClaimSummary.CardGroupSeparator />

            <ClaimSummary.CardGroupFields>
              {data.occurrence.sinister_date && (
                <ClaimSummary.CardAttribute>
                  <ClaimSummary.CardLabel>Fecha</ClaimSummary.CardLabel>
                  <ClaimSummary.CardValue>
                    {format(
                      new Date(data.occurrence.sinister_date!),
                      'dd/MM/yyyy HH:mm',
                    )}
                  </ClaimSummary.CardValue>
                </ClaimSummary.CardAttribute>
              )}

              {data.policyholder_license_plate && (
                <ClaimSummary.CardAttribute>
                  <ClaimSummary.CardLabel>
                    Patente asegurado
                  </ClaimSummary.CardLabel>
                  <ClaimSummary.CardValue>
                    {data.policyholder_license_plate}
                  </ClaimSummary.CardValue>
                </ClaimSummary.CardAttribute>
              )}

              {data.is_policyholder_vehicle_trailer && (
                <ClaimSummary.CardAttribute>
                  <ClaimSummary.CardLabel>¿Es trailer?</ClaimSummary.CardLabel>
                  <ClaimSummary.CardValue>
                    {data.is_policyholder_vehicle_trailer ? 'Si' : 'No'}
                  </ClaimSummary.CardValue>
                </ClaimSummary.CardAttribute>
              )}

              <ClaimSummary.CardAttribute>
                <ClaimSummary.CardLabel>País</ClaimSummary.CardLabel>
                <ClaimSummary.CardValue>
                  {data.occurrence.country
                    ? getCountryLabel(data.occurrence.country)
                    : '-'}
                </ClaimSummary.CardValue>
              </ClaimSummary.CardAttribute>

              {data.external_policyholder_number && (
                <ClaimSummary.CardAttribute>
                  <ClaimSummary.CardLabel>
                    Póliza asegurado
                  </ClaimSummary.CardLabel>
                  <ClaimSummary.CardValue>
                    {data.external_policyholder_number}
                  </ClaimSummary.CardValue>
                </ClaimSummary.CardAttribute>
              )}

              <ClaimSummary.OccurrenceLocationFields
                data={data.occurrence}
                canEdit={canEditOccurrenceLocation}
              />

              <ClaimSummary.CardAttribute>
                <ClaimSummary.CardLabel>Monto reclamado</ClaimSummary.CardLabel>
                <ClaimSummary.CardValue>
                  {currencyFormat(data.claimed_amount)}
                </ClaimSummary.CardValue>
              </ClaimSummary.CardAttribute>
              <ClaimSummary.CardAttribute>
                <ClaimSummary.CardLabel>
                  Zona de atención
                </ClaimSummary.CardLabel>
                <ClaimSummary.CardValue>
                  {desiredSupportLocationState?.name}
                </ClaimSummary.CardValue>
              </ClaimSummary.CardAttribute>
              {data.desired_support_location_state_municipality && (
                <ClaimSummary.CardAttribute>
                  <ClaimSummary.CardLabel>
                    Zona de atención (Municipio)
                  </ClaimSummary.CardLabel>
                  <ClaimSummary.CardValue>
                    {data.desired_support_location_state_municipality}
                  </ClaimSummary.CardValue>
                </ClaimSummary.CardAttribute>
              )}
              <ClaimSummary.CardAttribute className='col-span-2'>
                <ClaimSummary.CardLabel>
                  Forma de ocurrencia
                </ClaimSummary.CardLabel>
                <ClaimSummary.CardValue>
                  {data.occurrence.description}
                </ClaimSummary.CardValue>
              </ClaimSummary.CardAttribute>
            </ClaimSummary.CardGroupFields>

            <ClaimSummary.CardGroupSeparator />

            <ClaimSummary.CardGroupFields className={cn('grid grid-cols-1')}>
              <ClaimSummary.Occurrence
                data={data.occurrence}
                segment='occurrence'
              />
            </ClaimSummary.CardGroupFields>
          </ClaimSummary.CardGroup>
        )}

        {showCard('location') && (
          <ClaimSummary.CardGroup>
            <ClaimSummary.Header>
              <ClaimSummary.CardGroupLabel>
                <ClaimSummary.Icon>
                  <IconMapPin />
                </ClaimSummary.Icon>
                Ubicación del siniestro
              </ClaimSummary.CardGroupLabel>
            </ClaimSummary.Header>
            <ClaimSummary.CardGroupSeparator />
            <ClaimSummary.CardGroupFields className='grid grid-cols-1'>
              <ClaimSummary.CardAttribute>
                <ClaimSummary.CardLabel>
                  {[
                    toTitleCase(data.occurrence.address || ''),
                    toTitleCase(data.occurrence.city || ''),
                    toTitleCase(
                      getStateLabel(
                        data.occurrence.country || 'ARG',
                        data.occurrence.state || '',
                      ),
                    ),
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </ClaimSummary.CardLabel>
              </ClaimSummary.CardAttribute>
            </ClaimSummary.CardGroupFields>

            <ClaimSummary.CardGroupFields className={cn('grid grid-cols-1')}>
              {data.occurrence.latitude && data.occurrence.longitude ? (
                <SinisterLocationMap
                  locationData={{
                    country: data.occurrence.country,
                    state: data.occurrence.state,
                    city: data.occurrence.city,
                    address: data.occurrence.address || '',
                    zip_code: data.occurrence.zip_code,
                  }}
                  country={data.occurrence.country || 'ARG'}
                  latitude={data.occurrence.latitude}
                  longitude={data.occurrence.longitude}
                  height='30rem'
                />
              ) : (
                <p className='text-sm text-gray-500 text-center py-4'>
                  No se pudo obtener la ubicación del siniestro.
                </p>
              )}
            </ClaimSummary.CardGroupFields>
          </ClaimSummary.CardGroup>
        )}

        {showCard('intake') && intakeSubmission && (
          <ClaimIntakeCard
            intake={intakeSubmission}
            originOrganizationName={data.customer_organization?.name}
            onSave={withEdit ? onSaveIntake : undefined}
            fetchers={withEdit ? intakeFetchers : undefined}
          />
        )}
      </ClaimSummary.CardList>
    </ClaimUpdateProvider>
  );
};
